//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { O } from 'ts-toolbelt';
import { useDebouncedCallback } from 'use-debounce';

import ActionButton from '@components/atoms/action-button/ActionButton';
import { Icon } from '@components/atoms/icon/Icon';
import { InlineSpinner } from '@components/atoms/spinner/Spinner';
import {
  Input,
  Props as InputProps,
} from '@components/atoms/text-input/TextInput';
import VisuallyHiddenText from '@components/atoms/visually-hidden-text/VisuallyHiddenText';
import DescriptionAndValidationWrapper from '@components/molecules/description-and-validation-wrapper/DescriptionAndValidationWrapper';
import { useUniqueId } from '@hooks/useUniqueId';

interface IOwnProps {
  className?: string;
  handleClear?: () => void;
  handleFetch?: (searchTerm: string) => Promise<void>;
  /* An accessibility label to indicate what we are searching for. This label is not visible. */
  visuallyHiddenLabel: string;
  trackingIdPrefix: string;
}

type ModifiedInputProps = Omit<
  O.Required<InputProps, 'placeholder' | 'onChange'>,
  'helpText' | 'labelElement' | 'trackingId'
>;

export interface SearchBarProps extends IOwnProps, ModifiedInputProps {}

/**
 * The `SearchBar` molecule renders a `TextInput` atom with an included search icon, and optional
 * debounced fetching and clear behavior.
 *
 * The `SearchBar` will automatically debounce and call the `handleFetch` callback when the search
 * input changes, if `handleFetch` is defined.
 *
 * The clear behavior is provided by an `ActionButton` on the right side of the input, and is
 * included any time that `handleClear`is defined and a searchterm is entered.
 */
export const SearchBar = React.forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      className = '',
      errorText,
      handleClear,
      handleFetch,
      hasError = false,
      isDisabled = false,
      onChange,
      trackingIdPrefix,
      visuallyHiddenLabel,
      ...inputProps
    },
    forwardedRef,
  ) => {
    const inputId = useUniqueId('search-bar-label-id');
    const [isFetching, setIsFetching] = useState(false);
    const fallBackRef = useRef<HTMLInputElement>(null);
    const inputRef = forwardedRef || fallBackRef;

    const debouncedHandleFetch = useDebouncedCallback(
      async (searchTerm: string) => {
        if (handleFetch && searchTerm) {
          setIsFetching(true);
          await handleFetch(searchTerm);
          setIsFetching(false);
        }
      },
      500,
    );

    const handleUpdate = (searchTerm: string) => {
      onChange(searchTerm);
      void debouncedHandleFetch(searchTerm);
    };

    const handleClearAndRefocus = () => {
      handleClear?.();
      (inputRef as React.RefObject<HTMLInputElement>).current?.focus();
    };

    const rightContent = (() => {
      if (isFetching) {
        return <FetchingSpinner />;
      } else if (handleClear && !!inputProps.value) {
        return (
          <ClearButton
            label='Clear Search'
            onClick={handleClearAndRefocus}
            trackingId={`${trackingIdPrefix}-clear-button`}
          />
        );
      }
    })();

    const searchIconLabel = (
      <SearchIconLabel htmlFor={inputId}>
        <VisuallyHiddenText>{visuallyHiddenLabel}</VisuallyHiddenText>
        <Icon name='search' />
      </SearchIconLabel>
    );

    const renderInput = (descriptionTextId?: string) => (
      <CustomTextInput
        {...inputProps}
        aria-describedby={descriptionTextId}
        aria-invalid={hasError}
        disabled={isDisabled}
        hasError={hasError}
        id={inputId}
        onChange={(e) => handleUpdate(e.target.value)}
        ref={inputRef}
        trackingId={`${trackingIdPrefix}-input`}
      />
    );

    return (
      <DescriptionAndValidationWrapper
        className={className}
        customLabel={searchIconLabel}
        errorText={errorText}
        formComponentId={inputId}
        hasError={hasError}
        renderFormComponent={renderInput}
      >
        {rightContent}
      </DescriptionAndValidationWrapper>
    );
  },
);

SearchBar.displayName = 'SearchBar';

const CustomTextInput = styled(Input)(
  ({ theme }) => `
  position: relative;
  /* 16px on left of icon + icon + 8px on right of icon */
  padding-left: calc(${theme.spacings[3]} + ${theme.spacings[3]} + ${
    theme.spacings[2]
  });
  padding-right: ${theme.spacings[5]};
`,
);

const SearchIconLabel = styled.label(
  ({ theme }) => `
  position: absolute;
  line-height: 1;
  color: ${theme.colors.aliases.bodySecondary};
  top: ${theme.spacings[2]};
  left: ${theme.spacings[3]};
  z-index: 1;
`,
);

const ClearButton = styled(ActionButton).attrs({
  size: 'extra-small',
  iconName: 'close',
})(
  ({ theme }) => `
  position: absolute;
  top: ${theme.spacings[1]};
  right: ${theme.spacings[1]};
`,
);

const FetchingSpinner = styled(InlineSpinner)(
  ({ theme }) => `
  position: absolute;
  top: ${theme.spacings[2]};
  right: ${theme.spacings[2]};
`,
);

export default SearchBar;
